/**
 * 智能客服 - 渠道授权 接口定义
 */
import { apiGet, apiPost, apiPut, apiRequest } from '@/utils/request';
import { parseListResponse, parsePaginatedResponse } from '@/utils/apiResponse';
import { queryChannelsList, type ChannelListParams } from '@/api/channels';
import { useChatAccountsStore } from '@/stores/chatAccounts';
import type {
  ChannelAuthFilters,
  FeigeParentAccount,
  MeituanAuthCreated,
  MeituanAuthEvent,
  MeituanParentAccount,
  MeituanShopCard,
  MeituanSplitResult,
  WxkfAccount,
} from '@/types/channelManagement';

/** 把渠道授权页筛选条件转为账号列表接口 query 参数（剔除空字符串与 null，保留 status=0） */
export function buildChannelAccountQuery(filters: ChannelAuthFilters): Record<string, unknown> {
  const query: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === '' || value === null || value === undefined) continue;
    query[key] = value;
  }
  return query;
}

/** 账号新增、授权、改名、删除、解绑成功后让可见账号缓存失效，各处账号下拉下次取到最新数据 */
const withAccountsInvalidate = <T>(request: Promise<T>) =>
  request.then((res) => {
    useChatAccountsStore().invalidate();
    return res;
  });

export const channelsApi = {
  /** 渠道列表，复用通用接口；传 has_account=1 只要有账号的渠道 */
  list: (params?: ChannelListParams) => queryChannelsList(params),
  accounts: (channelId: number, params?: Record<string, unknown>) =>
    apiGet(`/channels/${channelId}/accounts`, params).then((d) => parseListResponse(d)),
  /** 渠道账号列表（分页） */
  accountsPaginated: (channelId: number, params?: Record<string, unknown>) =>
    apiGet(`/channels/${channelId}/accounts`, params).then((d) => parsePaginatedResponse(d)),
  /** 获取渠道账号详情，含完整 config。channelId 为渠道 ID（数字），参数与 updateAccount 一致 */
  accountDetail: (channelId: string, accountId: number) =>
    apiGet(`/channels/${channelId}/accounts/${accountId}`),
  createAccount: (channelId: number, data: Record<string, unknown>) =>
    withAccountsInvalidate(apiPost(`/channels/${channelId}/accounts`, data)),
  /** 新增 Web 渠道账号（专用接口，无需 channel_id），page_config 以 JSON 字符串形式传参 */
  createWebAccount: (data: {
    name: string;
    alias?: string;
    robot_id?: number;
    page_config?: string;
  }) => withAccountsInvalidate(apiPost('/channels/web/accounts', data)),
  authUrl: (channelId: number, accountId?: number) =>
    apiGet(`/channels/${channelId}/auth-url`, accountId ? { account_id: accountId } : {}).then(
      (d) => ((d as Record<string, unknown>)?.url ?? '') as string,
    ),
  /** 微信客服授权：获取回填参数（URL、Token、EncodingAESKey），GET /channels/:id/wechat-kf-params */
  getWechatKfParams: (channelId: number) =>
    apiGet(`/channels/${channelId}/wechat-kf-params`).then((d) => {
      const o = d as Record<string, unknown>;
      return {
        url: (o?.url ?? o?.callback_url ?? '') as string,
        token: (o?.token ?? '') as string,
        encodingAESKey: (o?.encoding_aes_key ?? o?.encodingAESKey ?? '') as string,
      };
    }),
  auth: (channelId: number, data: Record<string, unknown>) =>
    withAccountsInvalidate(apiPost(`/channels/${channelId}/auth`, data)),
  updateAccount: (channelId: number, accountId: number, data: Record<string, unknown>) =>
    withAccountsInvalidate(apiPut(`/channels/${channelId}/accounts/${accountId}`, data)),
  /** 解绑账号当前绑定的机器人，并清理机器人配置模块下的账号绑定 */
  unbindRobot: (accountId: number) =>
    apiPost('/channels/accounts/unbind-robot', { account_id: accountId }),
  deleteAccount: (channelId: number, accountId: number) =>
    withAccountsInvalidate(
      apiRequest(`/channels/${channelId}/accounts/${accountId}`, { method: 'DELETE' }),
    ),
  unbindAccount: (channelId: number, accountId: number) =>
    withAccountsInvalidate(apiPost(`/channels/${channelId}/accounts/${accountId}/unbind`)),
  /** 渠道 refresh_token 手动续签（抖音 / 小红书等共用） */
  refreshToken: (channelId: number, accountId: number) =>
    apiPost(`/channels/${channelId}/accounts/${accountId}/refresh-token`),
  /**
   * 微信客服 - 客服链接（对接 /wxkf/links 系列接口，按渠道账号 id 维度管理）
   */
  serviceLinks: {
    /** 链接列表（分页，按 id 排序） */
    list: (params: {
      account_id: number;
      page?: number;
      page_size?: number;
      open_kfid?: string;
      source?: string;
    }) => apiGet('/wxkf/links', params).then((d) => parsePaginatedResponse(d)),
    /** 企业下客服账号列表，用于新增链接时选择 open_kfid */
    kfAccounts: (account_id: number) =>
      apiGet('/wxkf/kf-accounts', { account_id }).then(
        (d) => (((d as Record<string, unknown>)?.list as WxkfAccount[]) ?? []) as WxkfAccount[],
      ),
    /**
     * 新增链接：调用企业微信 add_contact_way 生成并落库。
     * account_id 为渠道账号主键，open_kfid 为客服账号 ID（由用户在弹框中选择），source 为链接名称/来源备注
     */
    create: (account_id: number, open_kfid: string, source: string) =>
      apiPost('/wxkf/links', { account_id, open_kfid, source }),
    /** 编辑链接名称 */
    update: (id: number, source: string) => apiPut(`/wxkf/links/${id}`, { source }),
  },
  /**
   * 企业微信（个人号）：账号由后台手动创建，再由服务端页面绑定 RPA 设备。
   * 列表 / 改名 / 启停 / 绑机器人 / 绑知识库均走上面的通用接口。
   */
  qywx: {
    /** 新增账号，仅传名称；返回 auth_status=1（未绑定设备） */
    createAccount: (data: { name: string; alias?: string }) =>
      withAccountsInvalidate(apiPost('/channels/qywx/accounts', data)),
    /** 解除授权：仅解绑设备，账号保留，解绑后立即停止收发消息 */
    unbindDevice: (accountId: number) =>
      withAccountsInvalidate(apiPost(`/channels/qywx/accounts/${accountId}/unbind-device`)),
  },
  /**
   * 抖音飞鸽：授权产生「总店号」（主账号），实际接待广告主再拆分为其下的「广告主账户」（子账号）。
   * 子账号共享总店号 token，列表 / 改名 / 绑机器人 / 解除授权均走上面的通用接口。
   */
  feige: {
    /** 授权链接：与通用 authUrl 不同，飞鸽按广告主 ID（总店号）取链接，需前端先让用户填写 */
    authUrl: (channelId: number, advId: string) =>
      apiGet(`/channels/${channelId}/auth-url`, { adv_id: advId }).then(
        (d) => ((d as Record<string, unknown>)?.url ?? '') as string,
      ),
    /** 总店号下拉：返回已授权可用的主账号，供广告主拆分时选择 */
    parentAccounts: () =>
      apiGet('/feige/parent-accounts').then(
        (d) => (((d as Record<string, unknown>)?.list ?? []) as FeigeParentAccount[]) || [],
      ),
    /** 广告主拆分：把广告主 ID 拆成总店号下的子账号，接口幂等，返回实际拆分数与跳过数 */
    addAdvId: (id: number, advIds: string[]) =>
      withAccountsInvalidate(
        apiPost<{ created: number; skipped: number; error?: string }>('/feige/addAdvId', {
          id,
          adv_ids: advIds,
        }),
      ),
  },
  /**
   * 美团：走 RPA 客户端接入，授权流程为「建账号 → 进待授权池 → RPA 弹码 → 前端轮询结果」。
   * 总店与门店号各自持有一条授权，子号共享所属总店授权态，只能通过「拆分」创建。
   */
  meituan: {
    /** 发起授权：account_type 只能是 main（总店）或 store（门店号），子号走拆分 */
    authorize: (data: { name: string; account_type?: 'main' | 'store'; device_id?: string }) =>
      apiPost<MeituanAuthCreated>('/meituan/accounts/authorize', data),
    /** 轮询授权进度：二维码 / 登录失败 / 授权完成 */
    authEvent: (id: number) => apiGet<MeituanAuthEvent>(`/meituan/accounts/${id}/auth-event`),
    /** 清除授权事件缓存：关闭二维码弹窗或需要重新取码时调用 */
    clearAuthEvent: (id: number) => apiPost(`/meituan/accounts/${id}/auth-event/clear`),
    /** 重新授权：总店与门店号可用，子号会返回 422 */
    reauthorize: (id: number, data: { device_id?: string } = {}) =>
      apiPost(`/meituan/accounts/${id}/reauthorize`, data),
    /** 拆分总店子号：子号名称由后端按门店ID自动生成；shop_ids 按门店 ID 数组提交 */
    split: (data: { parent_id: number; shop_ids: string[] }) =>
      apiPost<MeituanSplitResult>('/meituan/accounts/split', data),
    /** 总店下拉（含子号），用于拆分时选择总店 */
    parentAccounts: () =>
      apiGet('/meituan/parent-accounts').then(
        (d) =>
          (((d as Record<string, unknown>)?.list ?? []) as MeituanParentAccount[]) ||
          ([] as MeituanParentAccount[]),
      ),
    /** 下发商品卡同步任务：仅总店 / 门店号可用，子号会返回 422；下发后旧缓存会被清空 */
    syncShopCard: (id: number) =>
      apiPost<{ requested: boolean; open_id_ready: boolean }>(
        `/meituan/accounts/${id}/sync-shop-card`,
        {},
      ),
    /** 拉取 RPA 上报的商品卡，同步任务完成前为空数组 */
    shopCards: (id: number) =>
      apiGet(`/meituan/accounts/${id}/shop-cards`).then(
        (d) => ((d as Record<string, unknown>)?.list ?? []) as MeituanShopCard[],
      ),
  },
};

export const queryChannels = () => channelsApi.list();
export const queryChannelAccounts = (channelId: number, params?: Record<string, unknown>) =>
  channelsApi.accounts(channelId, params);

/**
 * 抖音 RPA 授权（直播授权 / 直播数据授权）相关接口
 * 迁移自 conversation 项目 liveAuth.vue，沿用原 URL 风格（驼峰段名，无连字符）：
 *   - saveHtmlOperate: 触发扫码授权事件
 *   - getClientOperate: 轮询获取最新授权状态
 *   - mtSaveHtmlOperate / mtClientOperate: 美团专用
 */
export const rpaApi = {
  saveHtmlOperate: (data: Record<string, unknown>) => apiPost('/rpa/saveHtmlOperate', data),
  getClientOperate: (data: Record<string, unknown>) => apiPost('/rpa/getClientOperate', data),
  mtSaveHtmlOperate: (data: Record<string, unknown>) => apiPost('/rpa/mt/saveHtmlOperate', data),
  mtClientOperate: (data: Record<string, unknown>) => apiPost('/rpa/mt/getClientOperate', data),
};
